import React from 'react';
import { useSiteI18n } from '../SiteI18n';
import { SitePageHero } from '../components/SitePageParts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { SiteSEO } from '../SiteSEO';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Phone, MapPin, Clock, ShieldCheck, Sparkles, Calendar, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SiteContact: React.FC = () => {
  const { t, lang } = useSiteI18n();
  const [loading, setLoading] = React.useState(false);
  const [form, setForm] = React.useState<{ name: string; email: string; org: string; role: string; message: string }>({
    name: '', email: '', org: '', role: t.contact.roles[0], message: '',
  });

  React.useEffect(() => {
    document.title = lang === 'ro' ? 'Contact AETHERIS' : 'Contact AETHERIS';
  }, [lang]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({ title: t.contact.error, variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('site-lead-submit', {
        body: {
          name: form.name,
          email: form.email,
          organization: form.org || null,
          role_segment: form.role,
          message: form.message,
          lang,
          source_url: typeof window !== 'undefined' ? window.location.href : null,
        },
      });
      if (error || (data as any)?.error) throw error || new Error((data as any).error);
      toast({ title: t.contact.success });
      setForm({ name: '', email: '', org: '', role: t.contact.roles[0], message: '' });
    } catch (err) {
      console.error(err);
      toast({ title: t.contact.error, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SiteSEO
        path="/site/contact"
        title={t.contact.title + ' — AETHERIS'}
        description={t.contact.subtitle}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: t.contact.title,
            url: 'https://aetheris-is.com/site/contact',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: (lang === 'ro' ? [
              { q: 'Cât durează o implementare AETHERIS?', a: 'De la 24h pentru un cabinet medical sau o asociație de proprietari, până la 90 de zile pentru un Smart City complet la nivel de municipiu mare.' },
              { q: 'Datele mele rămân în Uniunea Europeană?', a: 'Da. Toate datele sunt stocate în eu-central-1 (Frankfurt). Nu folosim datele tale pentru antrenare AI și nu le partajăm cu terți.' },
              { q: 'Ce se întâmplă dacă vreau doar un singur modul?', a: 'Fiecare dintre cele 7 module este complet funcțional independent. Plătești doar pentru ce folosești.' },
              { q: 'Cum gestionați conformitatea NIS2 și GDPR?', a: 'NIS2 și GDPR sunt by design: RLS, MFA, runtime pentest, dashboard live, raportare automată DNSC, DPIA per modul.' },
              { q: 'Aveți integrare cu sistemele noastre existente?', a: 'Da: Open API REST, webhooks, SSO Google/Microsoft, ANAF, CNAS, Stripe, Resend, KNX/IP, Modbus, BACnet, OCPP.' },
              { q: 'Pot să încerc platforma înainte să cumpăr?', a: 'Da. Demo personalizat gratuit. Pentru organizații publice și spitale oferim pilot de 60 de zile fără cost.' },
              { q: 'Ce SLA garantați?', a: 'Uptime 99.98%, API p95 <200ms, suport 24/7 Enterprise, răspuns critic <4h pentru infrastructură publică și sănătate.' },
            ] : [
              { q: 'How long does an AETHERIS implementation take?', a: 'From 24h for a doctor’s office, up to 90 days for a full Smart City at municipal level.' },
              { q: 'Does my data stay in the European Union?', a: 'Yes. All data is stored in eu-central-1 (Frankfurt). We never use it for AI training.' },
              { q: 'What if I only want a single module?', a: 'Each of the 7 modules is fully functional on its own. You pay only for what you use.' },
              { q: 'How do you handle NIS2 and GDPR compliance?', a: 'NIS2 and GDPR by design: RLS, MFA, runtime pentest, live dashboard, automated CSIRT reporting, per-module DPIA.' },
              { q: 'Do you integrate with our existing systems?', a: 'Yes: Open REST API, webhooks, Google/Microsoft SSO, ANAF, Stripe, Resend, KNX/IP, Modbus, BACnet, OCPP.' },
              { q: 'Can I try the platform before buying?', a: 'Yes. Free tailored demo. Public organisations and hospitals receive a free 60-day pilot.' },
              { q: 'What SLA do you guarantee?', a: '99.98% uptime, API p95 <200ms, 24/7 Enterprise support, <4h critical response for public infrastructure.' },
            ]).map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
        ]}
      />
      <SitePageHero
        label={t.nav.contact}
        title={t.contact.title}
        subtitle={t.contact.subtitle}
        cta={false}
      />
      <section className="py-12">
        <div className="container mx-auto px-4 lg:px-6 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form
              onSubmit={submit}
              className="rounded-3xl border border-border/60 bg-card/40 backdrop-blur-md p-6 md:p-8 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{t.contact.name}</Label>
                  <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="email">{t.contact.email}</Label>
                  <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="org">{t.contact.org}</Label>
                  <Input id="org" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} />
                </div>
                <div>
                  <Label>{t.contact.role}</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {t.contact.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="message">{t.contact.message}</Label>
                <Textarea id="message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
              <Button type="submit" size="lg" disabled={loading} className="w-full sm:w-auto shadow-xl shadow-primary/30">
                {loading ? t.contact.sending : t.contact.send}
              </Button>
            </form>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-md p-6 hover:border-primary/40 transition-all">
              <Mail className="h-5 w-5 text-primary mb-3" />
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Email</div>
              <a href="mailto:hello@aetheris-is.com" className="font-medium hover:text-primary">hello@aetheris-is.com</a>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-md p-6 hover:border-primary/40 transition-all">
              <Phone className="h-5 w-5 text-primary mb-3" />
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{lang === 'ro' ? 'Telefon' : 'Phone'}</div>
              <a href="tel:+40752488258" className="font-medium hover:text-primary">+40 752 488 258</a>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-md p-6 hover:border-primary/40 transition-all">
              <MapPin className="h-5 w-5 text-primary mb-3" />
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{lang === 'ro' ? 'Sediu' : 'Office'}</div>
              <div className="font-medium">Timișoara, România</div>
            </div>

            <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-cyan-500/5 backdrop-blur-md p-6">
              <Clock className="h-5 w-5 text-primary mb-3" />
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{lang === 'ro' ? 'Promisiune' : 'Promise'}</div>
              <div className="font-bold text-sm leading-snug">
                {lang === 'ro' ? 'Răspundem în maxim 24h lucrătoare.' : 'We respond within 24 working hours.'}
              </div>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                {lang === 'ro'
                  ? 'Cererile critice (spitale, infrastructură publică) sunt prioritizate și primesc răspuns în <4h.'
                  : 'Critical requests (hospitals, public infrastructure) are prioritised and answered in <4h.'}
              </p>
            </div>

            <div className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-md p-6">
              <ShieldCheck className="h-5 w-5 text-primary mb-3" />
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{lang === 'ro' ? 'Confidențialitate' : 'Confidentiality'}</div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {lang === 'ro'
                  ? 'NDA gratuit la cerere. Datele tale rămân în UE și nu sunt folosite pentru antrenare AI.'
                  : 'Free NDA on request. Your data stays in the EU and is never used for AI training.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="py-16 bg-card/30 border-y border-border/60">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-1.5 mb-3 px-3 py-1 rounded-full bg-primary/10 text-primary border border-primary/30 text-xs">
              <Sparkles className="h-3 w-3" /> {lang === 'ro' ? 'Următorii pași' : 'What happens next'}
            </div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
              {lang === 'ro' ? 'Trei pași până la primul demo.' : 'Three steps to the first demo.'}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {(lang === 'ro' ? [
              { n: '01', icon: Mail, t: 'Trimiți mesajul', d: 'Ne spui cine ești și ce vrei să rezolvi. 2 minute.' },
              { n: '02', icon: Calendar, t: 'Programăm un call', d: 'Răspundem în 24h cu un slot și un agent specializat pe vertical-ul tău.' },
              { n: '03', icon: Sparkles, t: 'Demo personalizat', d: 'Vezi platforma rulând cu date relevante pentru organizația ta. Fără slide-uri vagi.' },
            ] : [
              { n: '01', icon: Mail, t: 'Send the message', d: 'Tell us who you are and what you want to solve. 2 minutes.' },
              { n: '02', icon: Calendar, t: 'We book a call', d: 'We reply in 24h with a slot and a vertical-specialised agent.' },
              { n: '03', icon: Sparkles, t: 'Tailored demo', d: 'See the platform running on data relevant to your org. No vague slides.' },
            ]).map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="relative rounded-2xl border border-border/60 bg-card/60 backdrop-blur-md p-6 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all"
              >
                <div className="text-[10px] font-black tracking-[0.2em] text-primary mb-2">{s.n}</div>
                <s.icon className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-bold mb-1.5">{s.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-6 max-w-4xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              {lang === 'ro' ? 'Întrebări frecvente' : 'Frequently asked'}
            </h2>
          </div>
          <FAQList lang={lang} />
        </div>
      </section>
    </>
  );
};

const FAQList: React.FC<{ lang: 'ro' | 'en' }> = ({ lang }) => {
  const items = lang === 'ro' ? [
    { q: 'Cât durează o implementare AETHERIS?', a: 'De la 24h pentru un cabinet medical sau o asociație de proprietari, până la 90 de zile pentru un Smart City complet la nivel de municipiu mare. Estimarea exactă o primești la primul call.' },
    { q: 'Datele mele rămân în Uniunea Europeană?', a: 'Da. Toate datele sunt stocate în eu-central-1 (Frankfurt). Nu folosim datele tale pentru antrenarea modelelor AI și nu le partajăm cu terți.' },
    { q: 'Ce se întâmplă dacă vreau doar un singur modul?', a: 'Fiecare dintre cele 7 module este complet funcțional independent. Plătești doar pentru ce folosești și activezi celelalte module când ai nevoie, fără migrare.' },
    { q: 'Cum gestionați conformitatea NIS2 și GDPR?', a: 'NIS2 și GDPR sunt by design: RLS pe toate tabelele sensibile, MFA, runtime pentest, dashboard live, raportare automată DNSC, DPIA per modul, drepturi subiect automatizate.' },
    { q: 'Aveți integrare cu sistemele noastre existente?', a: 'Da: Open API REST, webhooks, SSO Google/Microsoft, integrare nativă ANAF, CNAS, Stripe, Resend, KNX/IP, Modbus, BACnet, OCPP. Putem construi integrări custom în 2-4 săptămâni.' },
    { q: 'Pot să încerc platforma înainte să cumpăr?', a: 'Da. Demo personalizat gratuit pe datele tale. Pentru organizații publice și spitale oferim și un pilot de 60 de zile fără cost.' },
    { q: 'Cum se face billing-ul?', a: 'Pricing transparent, scalabil per modul și per utilizator activ. Contract anual cu plată trimestrială sau anuală. Pentru organizații publice acceptăm proceduri SEAP și PCT.' },
    { q: 'Ce SLA garantați?', a: 'Uptime 99.98% contractual, API p95 <200ms, suport 24/7 pentru clienții Enterprise, răspuns critic <4h pentru infrastructură publică și sănătate.' },
  ] : [
    { q: 'How long does an AETHERIS implementation take?', a: 'From 24h for a doctor’s office or owner association, up to 90 days for a full Smart City at the municipal level. The exact estimate comes after the first call.' },
    { q: 'Does my data stay in the European Union?', a: 'Yes. All data is stored in eu-central-1 (Frankfurt). We do not use your data to train AI models and we do not share it with third parties.' },
    { q: 'What if I only want a single module?', a: 'Each of the 7 modules is fully functional on its own. You only pay for what you use and activate other modules when you need them, with no migration.' },
    { q: 'How do you handle NIS2 and GDPR compliance?', a: 'NIS2 and GDPR are by design: RLS on every sensitive table, MFA, runtime pentest, live dashboard, automated CSIRT reporting, per-module DPIA, automated subject rights.' },
    { q: 'Do you integrate with our existing systems?', a: 'Yes: Open REST API, webhooks, Google/Microsoft SSO, native integrations with national tax/health insurers, Stripe, Resend, KNX/IP, Modbus, BACnet, OCPP. Custom integrations in 2-4 weeks.' },
    { q: 'Can I try the platform before buying?', a: 'Yes. Free tailored demo on your data. Public organisations and hospitals also receive a free 60-day pilot.' },
    { q: 'How does billing work?', a: 'Transparent pricing, scalable per module and per active user. Annual contract with quarterly or annual payment. We accept public procurement procedures.' },
    { q: 'What SLA do you guarantee?', a: '99.98% contractual uptime, API p95 <200ms, 24/7 support for Enterprise customers, <4h critical response for public infrastructure and healthcare.' },
  ];

  const [open, setOpen] = React.useState<number | null>(0);
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <motion.div
          key={it.q}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.03 }}
          className="rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md overflow-hidden"
        >
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-primary/5 transition-colors"
          >
            <span className="font-semibold text-sm md:text-base">{it.q}</span>
            <ChevronDown className={`h-4 w-4 text-primary shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">
                  {it.a}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>
  );
};

export default SiteContact;
